/**
 * Shared types for WorkflowRunDetail and all sub-components.
 * Moved here so they can be imported across run-detail/* without circular refs.
 */
import type { WorkflowRunDebuggerPayload } from '../../../generated/api/types.gen.js';

export type DetailLevel = 'summary' | 'debug' | 'full';

/**
 * Fan-out parent report + child unit (Spec 82 S6). Narrowed directly from the
 * generated WorkflowRunDebuggerPayload so the runtime contract stays the single
 * source of truth — no hand-rolled DTO. Only the whitelisted compact fields on
 * these types are ever surfaced in the UI (never raw child-agent prompts,
 * messages, logs, transcripts, or tool I/O).
 */
export type FanOutReport = NonNullable<WorkflowRunDebuggerPayload['graph']['steps'][number]['fanOut']>;
export type FanOutUnit = FanOutReport['units'][number];

/**
 * Source bundle metadata entry (Spec 82 source passthrough). Narrowed directly
 * from the generated WorkflowRunDebuggerPayload. METADATA ONLY — digestPrefix is
 * the first 12 chars of the SHA-256 digest; file contents are never present.
 */
export type SourceBundleMetaEntry = NonNullable<
  WorkflowRunDebuggerPayload['graph']['steps'][number]['sourceBundles']
>[number];

export interface DebugStep {
  id: string;
  stepId: string;
  stepName: string;
  kind: string;
  status: string;
  startedAt: string | null;
  completedAt: string | null;
  durationMs: number | null;
  retryCount: number;
  outputSummary: string;
  evidence?: unknown;
  fullIo?: unknown;
  error: string | null;
  verification: { [key: string]: unknown };
  contract: {
    compatible: boolean;
    addedFields: string[];
    removedFields: string[];
    changedTypes: Array<{ [key: string]: unknown }>;
  };
  /** Present only on fan-out parent steps. Non-fan-out steps leave this undefined. */
  fanOut?: FanOutReport;
  /** Present only on source-generating fan-out steps. Metadata only — no file contents. */
  sourceBundles?: SourceBundleMetaEntry[];
}

export interface DebugPayload {
  detail: DetailLevel;
  run: {
    id: string;
    engineRunId: string | null;
    workflowId: string | null;
    workflowName: string;
    workflowVersion: number | null;
    status: string;
    triggeredBy: string;
    startedAt: string | null;
    completedAt: string | null;
    durationMs: number | null;
    totalCostUsd: string;
    error: string | null;
    teamId?: string | null;
  };
  graph: {
    steps: DebugStep[];
    statusCounts: Record<string, number>;
  };
  timeline: Array<{
    at: string;
    type: string;
    /** Added by OBS-6 stage 1 server merge — 'llm_call' | 'tool_execution' | legacy event types */
    kind: string;
    label: string;
    severity: string;
    stepId: string | null;
    /** refId links to a specific llmCalls.calls[].id or tool execution id */
    refId: string | null;
  }>;
  /**
   * LLM call detail block — present only when the server returns the debug payload
   * with llmCalls populated (bounded to newest N calls; totalCount reflects full count).
   * `*_ref` content fields are only non-null for the CanManageWorkflows tier.
   */
  llmCalls?: {
    totalCount: number;
    cappedAt: number;
    calls: Array<{
      id: string;
      kind: 'llm_call';
      provider: string;
      model: string;
      modelVersion: string | null;
      stepId: string | null;
      agentId: string | null;
      temperature: string | null;
      maxTokens: number | null;
      promptTokens: number | null;
      completionTokens: number | null;
      costUsd: string | null;
      finishReason: string | null;
      durationMs: number | null;
      startedAt: string | null;
      traceId: string | null;
      systemPromptRef: string | null;
      messagesRef: string | null;
      responseRef: string | null;
    }>;
  };
  /**
   * linkedEntities: all six arrays are typed as Array<Record<string, unknown>> to match
   * the generated WorkflowRunDebuggerPayload (types.gen.ts lines 900-918), which uses
   * Array<{ [key: string]: unknown }> for every sub-array.  Accessing named properties
   * like .id/.name on the generated type bypasses TS safety — use safe accessors:
   *   String(item['name'] ?? '—')
   * Do NOT widen the generated type definition.
   */
  linkedEntities: {
    projects: Array<Record<string, unknown>>;
    tasks: Array<Record<string, unknown>>;
    workforce: Array<Record<string, unknown>>;
    templates: Array<Record<string, unknown>>;
    artifacts: Array<Record<string, unknown>>;
    decisions: Array<Record<string, unknown>>;
  };
  actionAvailability: Record<string, boolean>;
  audit: Array<{
    id: string;
    eventType: string;
    severity: string;
    stepId: string | null;
    createdAt: string | null;
    payload: unknown;
  }>;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

export function formatDuration(ms?: number | null): string {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const mins = Math.floor(ms / 60_000);
  const secs = Math.round((ms % 60_000) / 1000);
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
}

export function formatTime(iso?: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

export function formatDateShort(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatCost(raw: string | number | null | undefined): string {
  if (raw == null || raw === '') return '—';
  const n = typeof raw === 'number' ? raw : parseFloat(raw as string);
  if (Number.isNaN(n)) return '—';
  return `$${n.toFixed(2)}`;
}

export type StepStatusMapped = 'ok' | 'running' | 'failed' | 'skipped' | 'pending';

export function mapStepStatus(s: string): StepStatusMapped {
  switch (s) {
    case 'completed':
    case 'success':
      return 'ok';
    case 'running':
      return 'running';
    case 'failed':
      return 'failed';
    case 'skipped':
      return 'skipped';
    default:
      return 'pending';
  }
}

export function statusBadgeVariant(status: string): 'success' | 'danger' | 'accent' | 'neutral' | 'warning' {
  switch (status) {
    case 'completed':
    case 'success':
      return 'success';
    case 'failed':
      return 'danger';
    case 'running':
      return 'accent';
    case 'cancelled':
      return 'neutral';
    default:
      return 'neutral';
  }
}

export function findFailedStep(steps: DebugStep[]): DebugStep | undefined {
  return steps.find((s) => s.status === 'failed');
}

/** Duration in ms between two ISO timestamps, or null when either is missing/invalid. */
export function durationBetween(start?: string | null, end?: string | null): number | null {
  if (!start || !end) return null;
  const ms = new Date(end).getTime() - new Date(start).getTime();
  return Number.isFinite(ms) && ms >= 0 ? ms : null;
}

/** Compact one-liner for a fan-out parent: "5 units · 3 ok · 1 failed · 1 skipped". */
export function fanOutSummary(counts: FanOutReport['counts'], total: number): string {
  const order: Array<[keyof FanOutReport['counts'], string]> = [
    ['succeeded', 'ok'],
    ['failed', 'failed'],
    ['running', 'running'],
    ['pending', 'pending'],
    ['skipped', 'skipped'],
    ['retried', 'retried'],
  ];
  const parts = [`${total} unit${total !== 1 ? 's' : ''}`];
  for (const [key, label] of order) {
    if (counts[key] > 0) parts.push(`${counts[key]} ${label}`);
  }
  return parts.join(' · ');
}

/** Humanize raw status enum → display label (completed→succeeded, etc.) */
export function humanizeStatus(status: string): string {
  switch (status) {
    case 'completed':
    case 'success':
      return 'succeeded';
    case 'failed':
      return 'failed';
    case 'running':
      return 'running';
    case 'cancelled':
    case 'canceled':
      return 'cancelled';
    case 'pending':
      return 'pending';
    default:
      return status;
  }
}
